import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Loader2 } from "lucide-react";

export function LetterheadsDialog({ open, onOpenChange, letterheads, reload }) {
  const [name, setName] = useState("");
  const [header, setHeader] = useState(null);
  const [footer, setFooter] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) return toast.error("Give the letterhead a name");
    if (!header && !footer) return toast.error("Upload a header and/or footer image");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      if (header) fd.append("header", header);
      if (footer) fd.append("footer", footer);
      await api.post("/letterheads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Letterhead saved");
      setName(""); setHeader(null); setFooter(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async (l) => { await api.delete(`/letterheads/${l.id}`); toast.success("Removed"); reload(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-xl">Letterheads (headers & footers)</DialogTitle></DialogHeader>
        <div className="space-y-2" data-testid="letterhead-list">
          {letterheads.map((l) => (
            <div key={l.id} className="flex items-center justify-between border border-border rounded-md px-3 py-2 text-sm" data-testid={`letterhead-row-${l.id}`}>
              <span>{l.name}{l.builtin && <span className="ml-2 text-[10px] uppercase text-muted-foreground">default</span>}</span>
              {!l.builtin && <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => del(l)} data-testid={`letterhead-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-4 space-y-3">
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">New letterhead name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Bocally Gas Exchange" data-testid="letterhead-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Header image (PNG/JPG)</Label><Input type="file" accept="image/*" onChange={(e) => setHeader(e.target.files?.[0] || null)} data-testid="letterhead-header-file" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Footer image (PNG/JPG)</Label><Input type="file" accept="image/*" onChange={(e) => setFooter(e.target.files?.[0] || null)} data-testid="letterhead-footer-file" /></div>
          </div>
          <Button onClick={save} disabled={busy} className="bg-primary hover:bg-[#C81219] text-white w-full" data-testid="letterhead-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save letterhead"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
